<script setup lang="ts">
import { ref, computed } from 'vue'
import { VIcon } from '@/modules/shared/components'

interface Agreement {
  id: number
  filename: string
  url: string
  created_at: string
}

interface Props {
  agreement: Agreement | null
  disabled?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update', payload: { file?: File; remove?: boolean }): void
}>()

const selectedFile = ref<File | null>(null)
const markedForRemoval = ref(false)
const isDragging = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const showCurrentAgreement = computed(() => {
  if (props.disabled && props.agreement) return true
  return props.agreement && !markedForRemoval.value && !selectedFile.value
})

const handleDragOver = (e: DragEvent) => {
  if (props.disabled) return
  e.preventDefault()
  isDragging.value = true
}

const handleDragLeave = () => {
  isDragging.value = false
}

const handleDrop = (e: DragEvent) => {
  if (props.disabled) return
  e.preventDefault()
  isDragging.value = false
  if (e.dataTransfer?.files?.length) {
    selectedFile.value = e.dataTransfer.files[0]
    markedForRemoval.value = false
    emit('update', { file: e.dataTransfer.files[0] })
  }
}

const handleFileSelect = (e: Event) => {
  const input = e.target as HTMLInputElement
  if (input.files?.length) {
    selectedFile.value = input.files[0]
    markedForRemoval.value = false
    emit('update', { file: input.files[0] })
    input.value = ''
  }
}

const openFileDialog = () => {
  if (props.disabled) return
  fileInput.value?.click()
}

const removeAgreement = () => {
  selectedFile.value = null
  markedForRemoval.value = true
  emit('update', { remove: true })
}

const clearSelection = () => {
  selectedFile.value = null
  markedForRemoval.value = false
  emit('update', {})
}

const reset = () => {
  selectedFile.value = null
  markedForRemoval.value = false
}

defineExpose({ reset })
</script>

<template>
  <!-- Current Agreement Display -->
  <div
    v-if="showCurrentAgreement"
    class="mt-3 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
  >
    <div class="flex items-center space-x-3">
      <VIcon name="PdfTypeDefault" :size="20" class="shrink-0" />
      <a :href="props.agreement?.url" target="_blank" class="text-sm font-medium text-[#3b88af] hover:underline">
        {{ props.agreement?.filename }}
      </a>
    </div>
    <button v-if="!props.disabled" type="button" @click="removeAgreement" class="text-red-600 hover:text-red-800">
      <VIcon name="Trash" :size="18" />
    </button>
  </div>

  <!-- New file selected -->
  <div
    v-else-if="selectedFile"
    class="mt-3 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-4 py-3"
  >
    <div class="flex items-center space-x-3">
      <VIcon name="PdfTypeDefault" :size="20" class="shrink-0" />
      <span class="text-sm font-medium text-gray-900">{{ selectedFile.name }}</span>
    </div>
    <button type="button" @click="clearSelection" class="text-red-600 hover:text-red-800">
      <VIcon name="Trash" :size="18" />
    </button>
  </div>

  <!-- File Upload Area -->
  <div
    v-else
    class="mt-3 rounded-md border-2 border-dashed p-6 text-center transition-colors"
    :class="[isDragging ? 'border-[#408F92] bg-[#408F92]/5' : 'border-gray-300', props.disabled ? 'opacity-50' : '']"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <VIcon name="Download" :size="32" class="mx-auto rotate-180 stroke-gray-400" />
    <p class="mt-2 text-sm text-gray-600">
      <template v-if="props.disabled">No file uploaded</template>
      <template v-else>
        Drag and drop a file here, or
        <button type="button" @click="openFileDialog" class="font-medium text-[#3b88af] hover:underline">browse</button>
      </template>
    </p>
    <p v-if="!props.disabled" class="mt-1 text-xs text-gray-500">PDF, DOC, or DOCX files accepted</p>
    <input ref="fileInput" type="file" class="hidden" accept=".pdf,.doc,.docx" @change="handleFileSelect" />
  </div>
</template>
